import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import * as Dialog from "@radix-ui/react-dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { X, ChevronLeft, ChevronRight, ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/lib/components/ui/button";
import { Badge } from "@/lib/components/ui/badge";
import { Input } from "@/lib/components/ui/input";
import { Skeleton } from "@/lib/components/ui/skeleton";
import { fetchFilament, addFilamentToCart } from "@/lib/catalogApi";
import { useCart } from "@/features/cart/hooks/useCart";
import { formatCurrency } from "@/lib/format";
import type { RetailFilamentRoll } from "@/types/catalog";

function getDiscountedPrice(grossPrice: number, discountPercent: number): number {
  return Math.round(grossPrice * (1 - discountPercent / 100) * 100) / 100;
}

function FilamentDetailSkeleton() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
      {/* Images column */}
      <div className="space-y-3">
        <Skeleton className="w-full aspect-square rounded-lg" />
        <div className="grid grid-cols-4 gap-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="aspect-square rounded" />
          ))}
        </div>
      </div>

      {/* Details column */}
      <div className="space-y-5">
        <div className="space-y-2">
          <Skeleton className="h-8 w-3/4" />
          <div className="flex gap-2 mt-2">
            <Skeleton className="h-5 w-16 rounded-full" />
            <Skeleton className="h-5 w-20 rounded-full" />
          </div>
        </div>
        {/* Color swatch */}
        <div className="flex items-center gap-3">
          <Skeleton className="h-8 w-8 rounded-full" />
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-16" />
        </div>
        {/* Weight */}
        <Skeleton className="h-4 w-32" />
        {/* Description */}
        <div className="space-y-2">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-2/3" />
        </div>
        {/* Price */}
        <Skeleton className="h-10 w-1/3" />
        {/* Qty + button */}
        <div className="flex gap-3">
          <Skeleton className="h-10 w-20" />
          <Skeleton className="h-10 flex-1" />
        </div>
      </div>
    </div>
  );
}

export default function FilamentDetailPage() {
  const { t } = useTranslation("catalog");
  const { slug } = useParams<{ slug: string }>();
  const [roll, setRoll] = useState<RetailFilamentRoll | null>(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [lbOpen, setLbOpen] = useState(false);
  const [lbIndex, setLbIndex] = useState(0);
  const { increment } = useCart();

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    fetchFilament(slug)
      .then(setRoll)
      .catch(() => toast.error(t("filaments.failedToLoadDetail")))
      .finally(() => setLoading(false));
  }, [slug]);

  const images = roll?.images ?? [];

  const goto = useCallback(
    (dir: -1 | 1) => setLbIndex(i => (i + dir + images.length) % images.length),
    [images.length],
  );

  useEffect(() => {
    if (!lbOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") { e.preventDefault(); goto(1); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); goto(-1); }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [lbOpen, goto]);

  const handleAddToCart = async () => {
    if (!roll) return;
    try {
      await addFilamentToCart(roll.id, quantity);
      increment(quantity);
      const label = roll.name ?? roll.color_name;
      toast.success(t("filaments.addedToCart", { name: label }));
    } catch {
      toast.error(t("filaments.failedToAdd"));
    }
  };

  const hasDiscount = (roll?.discount_percent ?? 0) > 0;
  const discountedPrice = hasDiscount && roll
    ? getDiscountedPrice(roll.gross_price, roll.discount_percent)
    : roll?.gross_price ?? 0;

  return (
    <div className="min-h-[calc(100vh-64px)] bg-muted text-foreground">
      <div className="max-w-5xl mx-auto px-6 py-10">
        <Link
          to={roll?.brand?.slug ? `/filaments?brand=${roll.brand.slug}` : "/filaments"}
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6"
        >
          <ArrowLeft className="h-4 w-4" />
          {t("filaments.backToFilaments")}
        </Link>

        {loading ? (
          <FilamentDetailSkeleton />
        ) : !roll ? (
          <p className="text-muted-foreground py-12 text-center">{t("filaments.notFound")}</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {/* Images */}
            <div className="space-y-3">
              {images.length > 0 ? (
                <>
                  <button
                    type="button"
                    onClick={() => { setLbIndex(0); setLbOpen(true); }}
                    className="block w-full rounded-lg overflow-hidden border border-border bg-card"
                    aria-label="View image gallery"
                  >
                    <img
                      src={images[0].url}
                      alt={images[0].alt ?? roll.color_name}
                      className="w-full aspect-square object-cover"
                    />
                  </button>
                  {images.length > 1 && (
                    <div className="grid grid-cols-4 gap-2">
                      {images.slice(1, 5).map((img, idx) => (
                        <button
                          key={img.id}
                          type="button"
                          onClick={() => { setLbIndex(idx + 1); setLbOpen(true); }}
                          className="rounded overflow-hidden border border-border bg-card"
                          aria-label={`View image ${idx + 2}`}
                        >
                          <img
                            src={img.url}
                            alt={img.alt ?? roll.color_name}
                            className="w-full aspect-square object-cover"
                            loading="lazy"
                          />
                        </button>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div
                  className="w-full aspect-square rounded-lg border border-border flex items-center justify-center"
                  style={{ backgroundColor: roll.color_hex || "#cccccc" }}
                >
                  <span className="text-sm font-medium text-white drop-shadow">
                    {roll.color_name}
                  </span>
                </div>
              )}
            </div>

            {/* Details */}
            <div className="space-y-5">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold">
                  {roll.name ?? roll.color_name}
                </h1>
                <div className="flex flex-wrap gap-2 mt-2">
                  {roll.brand && (
                    <Badge variant="outline">{roll.brand.name}</Badge>
                  )}
                  {roll.material && (
                    <Badge variant="outline">{roll.material.name}</Badge>
                  )}
                  {hasDiscount && (
                    <Badge className="bg-destructive text-destructive-foreground">
                      {roll.discount_percent}% OFF
                    </Badge>
                  )}
                </div>
              </div>

              {/* Color swatch */}
              <div className="flex items-center gap-3">
                <span
                  className="inline-block h-8 w-8 rounded-full border border-border shadow-sm"
                  style={{ backgroundColor: roll.color_hex || "#cccccc" }}
                  aria-label={roll.color_name}
                />
                <span className="text-sm font-medium">{roll.color_name}</span>
                {roll.color_hex && (
                  <span className="text-xs text-muted-foreground font-mono">{roll.color_hex}</span>
                )}
              </div>

              <div className="text-sm text-muted-foreground">
                {t("filaments.weight")}: <span className="font-medium text-foreground">{roll.weight_grams}g</span>
              </div>

              {roll.description && (
                <p className="text-muted-foreground leading-relaxed">{roll.description}</p>
              )}

              <div className="flex items-baseline gap-3">
                {hasDiscount ? (
                  <>
                    <span className="text-3xl font-extrabold">
                      {formatCurrency(discountedPrice)}
                    </span>
                    <span className="text-lg line-through text-muted-foreground">
                      {formatCurrency(roll.gross_price)}
                    </span>
                  </>
                ) : (
                  <span className="text-3xl font-extrabold">
                    {formatCurrency(roll.gross_price)}
                  </span>
                )}
                <span className="text-sm text-muted-foreground">{t("filaments.inclVat")}</span>
              </div>

              <div className="flex items-center gap-3">
                <Input
                  type="number"
                  min={1}
                  value={quantity}
                  onChange={e => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-20 text-center"
                />
                <Button size="lg" className="flex-1" onClick={handleAddToCart}>
                  {t("filaments.addToCart")}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Lightbox — only mounted when roll is loaded */}
      {roll && (
        <Dialog.Root open={lbOpen} onOpenChange={o => !o && setLbOpen(false)}>
          <Dialog.Portal>
            <Dialog.Overlay className="fixed inset-0 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0" />
          </Dialog.Portal>
          <Dialog.Portal>
            <Dialog.Content
              className="fixed inset-0 flex flex-col"
              aria-labelledby="filament-lb-title"
              aria-describedby="filament-lb-desc"
            >
              <VisuallyHidden>
                <Dialog.Title id="filament-lb-title">
                  {roll.name ?? roll.color_name} — Images
                </Dialog.Title>
                <Dialog.Description id="filament-lb-desc">
                  Image {lbIndex + 1} of {images.length}
                </Dialog.Description>
              </VisuallyHidden>

              {/* Top bar */}
              <div className="pointer-events-none absolute left-0 right-0 top-16 p-4 flex items-center justify-between text-white">
                <div className="pointer-events-auto bg-black/40 rounded-full px-3 py-1 text-xs md:text-sm">
                  {roll.name ?? roll.color_name}
                </div>
                <div className="flex items-center gap-2">
                  <div className="pointer-events-auto bg-black/40 rounded-full px-3 py-1 text-xs md:text-sm">
                    {String(lbIndex + 1).padStart(2, "0")} / {String(images.length).padStart(2, "0")}
                  </div>
                  <Dialog.Close asChild>
                    <button
                      aria-label="Close gallery"
                      className="pointer-events-auto inline-flex h-9 w-9 items-center justify-center rounded-full bg-black/40 hover:bg-black/60"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </Dialog.Close>
                </div>
              </div>

              {/* Image area */}
              <div className="flex-1 flex items-center justify-center px-4">
                {images[lbIndex] && (
                  <figure className="max-w-[min(92vw,1400px)] max-h-[82vh] flex flex-col items-center">
                    <img
                      key={lbIndex}
                      src={images[lbIndex].url}
                      alt={images[lbIndex].alt ?? roll.color_name}
                      className="max-w-full max-h-[72vh] object-contain select-none"
                      draggable={false}
                      decoding="async"
                    />
                    {images[lbIndex].caption && (
                      <figcaption className="mt-3 text-center text-xs md:text-sm text-white/90">
                        {images[lbIndex].caption}
                      </figcaption>
                    )}
                  </figure>
                )}
              </div>

              {/* Nav buttons */}
              {images.length > 1 && (
                <>
                  <button
                    aria-label="Previous image"
                    onClick={() => goto(-1)}
                    className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 inline-flex h-10 w-10 md:h-12 md:w-12 items-center justify-center rounded-full bg-black/40 hover:bg-black/60 text-white"
                  >
                    <ChevronLeft className="h-6 w-6 md:h-7 md:w-7" />
                  </button>
                  <button
                    aria-label="Next image"
                    onClick={() => goto(1)}
                    className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 inline-flex h-10 w-10 md:h-12 md:w-12 items-center justify-center rounded-full bg-black/40 hover:bg-black/60 text-white"
                  >
                    <ChevronRight className="h-6 w-6 md:h-7 md:w-7" />
                  </button>
                </>
              )}
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      )}
    </div>
  );
}
